import type { ProductFormulation } from '@/constants/products';
import { ProductFormulations } from '@/constants/products';

export const MAX_DAILY_DOSE_LABEL = 'Maximum daily dose';

/** The window a calculated dispense amount covers: one 30-day period, whatever the script's repeats */
export const DISPENSE_PERIOD_DAYS = 30;

/** Precision of a dispense amount: it is rounded up to this many decimal places and shown at the same */
export const DISPENSE_AMOUNT_DECIMAL_PLACES = 1;

/**
 * Units seen in product dosage text, each as its spellings: the singular, then a counted unit's plural. Pairing them
 * here, rather than deriving a singular from the spelling, keeps an irregular plural ('gummies') one unit with its
 * singular.
 */
export const DOSAGE_UNIT_SPELLINGS = [
  ['g'],
  ['mg'],
  ['mL'],
  ['unit', 'units'],
  ['capsule', 'capsules'],
  ['cartridge', 'cartridges'],
  ['gummy', 'gummies'],
  ['wafer', 'wafers'],
  ['pastille', 'pastilles'],
  ['tablet', 'tablets'],
  ['chew', 'chews'],
  ['spray', 'sprays'],
  ['puff', 'puffs'],
  ['drop', 'drops'],
  ['lozenge', 'lozenges'],
] as const;

/** Every spelling in DOSAGE_UNIT_SPELLINGS */
export const DOSAGE_UNITS = DOSAGE_UNIT_SPELLINGS.flat();

/** A unit seen in product dosage text, spelled as listed in DOSAGE_UNITS */
export type DosageUnit = (typeof DOSAGE_UNITS)[number];

/** Formulations whose pad items get a maximum daily dose */
export const MAX_DAILY_DOSE_FORMULATIONS: ReadonlySet<ProductFormulation> = new Set([ProductFormulations.HERB_DRIED]);

/**
 * Formulations that get a calculated dispense amount and a dispense limit on the prescription pad. Only Herb, Dried for
 * now: it is the only formulation pms-service supplies a net weight for, so the only one with an amount to limit.
 */
export const DISPENSE_LIMIT_FORMULATIONS: ReadonlySet<ProductFormulation> = new Set([ProductFormulations.HERB_DRIED]);

/**
 * Unit a formulation is measured in, fixed per formulation, shown against its maximum daily dose and dispense limit.
 * Formulations missing here show the number alone.
 */
export const UNIT_BY_FORMULATION: ReadonlyMap<ProductFormulation, DosageUnit> = new Map<ProductFormulation, DosageUnit>(
  [
    [ProductFormulations.HERB_DRIED, 'g'],
    [ProductFormulations.ORAL_LIQUID, 'mL'],
    [ProductFormulations.INHALATION, 'cartridges'],
    [ProductFormulations.PASTILLE, 'units'],
    [ProductFormulations.PESSARY, 'units'],
    [ProductFormulations.TABLET_EFFERVESCENT, 'units'],
    [ProductFormulations.CAPSULE, 'units'],
    [ProductFormulations.DEVICE, 'cartridges'],
    [ProductFormulations.TABLET_CHEWABLE, 'units'],
  ],
);
